import { useRef, useState } from 'react'
import {
  cloudOrder,
  cloudSize,
  cloudTerms,
  displayFor,
  scaleMean,
  type ItemDisplay,
  type ResultItem,
} from '../lib/surveys'
import { useHubLang } from '../lib/i18n'
import { SURVEYS } from '../lib/hubCopy'

/**
 * One survey question's answers, drawn as a word cloud, a list or bars (310).
 *
 * The same component on the presenter's screen (`big`) and on the results
 * page. What it may offer is decided by the caller from the SERVER's answer:
 * `owner` only when survey_live_results said my_access = 'owner', and the
 * hide functions refuse anybody else anyway.
 *
 * The cloud is flex-wrapped words, biggest in the middle — not a spiral
 * layout. It needs no library, never overlaps, and re-flows on a phone.
 */
export function SurveyItemResult({
  item,
  display,
  showNames = false,
  owner = false,
  big = false,
  onHideWord,
  onHideAnswer,
}: {
  item: ResultItem
  /** The presenter's choice; falls back to the item's own display. */
  display?: ItemDisplay
  showNames?: boolean
  owner?: boolean
  big?: boolean
  onHideWord?: (word: string, hidden: boolean) => void
  onHideAnswer?: (answerId: string, hidden: boolean) => void
}) {
  const t = SURVEYS[useHubLang()]
  const [picked, setPicked] = useState<string | null>(null)
  const textual = item.kind === 'words' || item.kind === 'text' || item.kind === 'ideas'
  const shown = displayFor(item.kind, display && (textual || display === 'bars') ? display : item.display)
  const answers = item.answers ?? []
  // 317. Pills that arrived since the last refresh pop in; the first draw does not.
  const seen = useRef<Set<string> | null>(null)

  if (textual && answers.filter((a) => !a.hidden).length === 0 && !(owner && answers.length)) {
    return <p className="surveyres__empty">{t.noAnswers}</p>
  }

  if (textual && shown === 'cloud') {
    const terms = cloudTerms(item.kind, answers, item.hiddenWords)
    const top = terms[0]?.count ?? 1
    return (
      <div className={`surveyres surveyres--cloud${big ? ' surveyres--big' : ''}`}>
        <ul className="surveycloud" aria-label={item.prompt}>
          {cloudOrder(terms).map((term, i) => (
            <li key={term.word}>
              {owner && onHideWord ? (
                <button
                  type="button"
                  className={`surveycloud__word surveycloud__word--c${i % 5}`}
                  style={{ fontSize: `calc(${cloudSize(term.count, top, big ? 1.2 : 0.95, big ? 5.5 : 3)}rem * var(--cloud-scale, 1))` }}
                  title={`${term.word}: ${term.count}`}
                  onClick={() => setPicked(term.word)}
                >
                  {term.word}
                </button>
              ) : (
                <span
                  className={`surveycloud__word surveycloud__word--c${i % 5}`}
                  style={{ fontSize: `calc(${cloudSize(term.count, top, big ? 1.2 : 0.95, big ? 5.5 : 3)}rem * var(--cloud-scale, 1))` }}
                  title={`${term.word}: ${term.count}`}
                >
                  {term.word}
                </span>
              )}
            </li>
          ))}
        </ul>
        {owner && picked && onHideWord && (
          <p className="surveyres__confirm">
            Take “{picked}” off the cloud?{' '}
            <button type="button" className="btn btn--sm btn--quiet" onClick={() => { onHideWord(picked, true); setPicked(null) }}>
              Hide it
            </button>{' '}
            <button type="button" className="btn btn--sm btn--link" onClick={() => setPicked(null)}>
              Keep it
            </button>
          </p>
        )}
        {owner && onHideWord && item.hiddenWords.length > 0 && (
          <p className="surveyres__hidden">
            Hidden:{' '}
            {item.hiddenWords.map((w) => (
              <button key={w} type="button" className="btn btn--xs btn--link" onClick={() => onHideWord(w, false)} title="Show it again">
                {w} ↺
              </button>
            ))}
          </p>
        )}
      </div>
    )
  }

  if (item.kind === 'ideas' && shown !== 'list') {
    const counted = new Map<string, number>()
    const keyed = answers.map((a) => {
      const n = (counted.get(a.text) ?? 0) + 1
      counted.set(a.text, n)
      return { a, key: a.answerId ?? `${a.text}#${n}` }
    })
    const before = seen.current
    seen.current = new Set(keyed.map((k) => k.key))
    return (
      <ul className={`surveypills${big ? ' surveypills--big' : ''}`} aria-label={item.prompt}>
        {keyed.map(({ a, key }) => (
          <li
            key={key}
            className={`surveypill${a.hidden ? ' surveypill--hidden' : ''}${before && !before.has(key) ? ' surveypill--new' : ''}`}
          >
            <span className="surveypill__text">{a.text}</span>
            {showNames && a.name && <span className="surveypill__name">{a.name}</span>}
            {owner && a.answerId && onHideAnswer && (
              <button
                type="button"
                className="surveypill__x"
                aria-label={a.hidden ? `Show “${a.text}” again` : `Hide “${a.text}”`}
                title={a.hidden ? 'Show it again' : 'Hide it'}
                onClick={() => onHideAnswer(a.answerId!, !a.hidden)}
              >
                {a.hidden ? '↺' : '×'}
              </button>
            )}
          </li>
        ))}
      </ul>
    )
  }

  if (textual) {
    return (
      <ul className={`surveylist${big ? ' surveylist--big' : ''}`}>
        {answers.map((a, i) => (
          <li key={a.answerId ?? i} className={a.hidden ? 'surveylist__row surveylist__row--hidden' : 'surveylist__row'}>
            <p className="surveylist__text">{a.text}</p>
            {(showNames && a.name) || (owner && a.answerId && onHideAnswer) ? (
              <p className="surveylist__meta">
                {showNames && a.name && <span>{a.name}</span>}
                {owner && a.answerId && onHideAnswer && (
                  <button type="button" className="btn btn--xs btn--link" onClick={() => onHideAnswer(a.answerId!, !a.hidden)}>
                    {a.hidden ? 'Show' : 'Hide'}
                  </button>
                )}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    )
  }

  // Bars: choices, or the five points of a rating.
  const rows =
    item.kind === 'scale'
      ? (item.scale ?? [0, 0, 0, 0, 0]).map((n, i) => ({ label: String(i + 1), n }))
      : (item.counts ?? item.choices.map(() => 0)).map((n, i) => ({ label: item.choices[i] ?? `#${i + 1}`, n }))
  const most = Math.max(1, ...rows.map((r) => r.n))
  const total = rows.reduce((a, r) => a + r.n, 0)
  const mean = item.kind === 'scale' ? scaleMean(item.scale) : null
  return (
    <div className={`surveyres surveyres--bars${big ? ' surveyres--big' : ''}`}>
      <ul className="surveybars">
        {rows.map((r) => (
          <li key={r.label} className="surveybars__row">
            <span className="surveybars__label">{r.label}</span>
            <span className="surveybars__track">
              <span className="surveybars__fill" style={{ inlineSize: `${(r.n / most) * 100}%` }} />
            </span>
            <span className="surveybars__n">
              {r.n}
              {total > 0 && item.kind === 'single' ? ` · ${Math.round((r.n / total) * 100)}%` : ''}
            </span>
          </li>
        ))}
      </ul>
      {mean !== null && <p className="surveyres__mean">{t.average(mean.toFixed(1))}</p>}
    </div>
  )
}
